/**
 * CoastalBiome - The fifth biome (2000-2500 miles)
 *
 * Coastal characteristics:
 * - Sun-soaked beach highway with ocean views
 * - Sandy cliffs and palm trees
 * - Winding roads along dramatic coastline
 * - Bright, warm color palette with ocean blues
 */

import { BiomeRenderer } from '../BiomeRenderer.js';
import { createBiomeConfig, validateBiomeConfig } from './BiomeConfig.js';

// Coastal-specific color palettes for each time of day
// Index: 0=Day, 1=Sunset, 2=Night, 3=Dawn
export const COASTAL_COLORS = [
    // ═══════════════════════════════════════════════════════════════════════════
    // DAY - Bright sandy beaches and ocean blues
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Sandy beach tones
            0xd2b48c, 0xc4a67a, 0xe0c8a0,
            // Coastal grass greens
            0x7a9a6a, 0x8aaa7a, 0x6a8a5a,
            // Rocky cliff grays
            0x9a9080, 0x8a8070, 0xaaa090
        ],
        rockColors: [
            0xa09080,   // Beach rock
            0xb0a090,   // Sandstone
            0x908070,   // Coastal gray
            0xc0b0a0    // Light boulder
        ],
        shrubColors: [
            0x4a7a4a,   // Coastal green
            0x5a8a5a,   // Palm green
            0x3a6a3a,   // Dark palm
            0x6a9a6a    // Light tropical
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SUNSET - Golden hour on the coast
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Golden sand
            0xe0a060, 0xd09050, 0xf0b070,
            // Warm cliff faces
            0xc08060, 0xb07050, 0xd09070,
            // Orange-lit vegetation
            0x8a8a50, 0x9a9a60, 0x7a7a40
        ],
        rockColors: [
            0xb08060,   // Warm rock
            0xc09070,   // Sunset stone
            0xa07050,   // Amber rock
            0xd0a080    // Golden boulder
        ],
        shrubColors: [
            0x5a7a40,   // Sunset palm
            0x6a8a50,   // Warm green
            0x4a6a30,   // Shadowed
            0x7a9a60    // Golden-green
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NIGHT - Moonlit beach
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Silver-blue sand
            0x6a7a8a, 0x5a6a7a, 0x7a8a9a,
            // Dark vegetation
            0x3a4a4a, 0x4a5a5a, 0x2a3a3a,
            // Night cliffs
            0x4a5058, 0x5a6068, 0x3a4048
        ],
        rockColors: [
            0x5a5a60,   // Night rock
            0x6a6a70,   // Moonlit stone
            0x4a4a50,   // Dark rock
            0x7a7a80    // Pale boulder
        ],
        shrubColors: [
            0x1a2a2a,   // Night palm
            0x2a3a3a,   // Dark tropical
            0x0a1a1a,   // Deep shadow
            0x2a3a38    // Moonlit frond
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DAWN - Soft pink morning on the coast
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Pink-tinged sand
            0xc0a8a0, 0xd0b8b0, 0xb09890,
            // Soft morning greens
            0x7a8a78, 0x8a9a88, 0x6a7a68,
            // Cool cliff tones
            0x9a9898, 0x8a8888, 0xaaa8a8
        ],
        rockColors: [
            0x9a9090,   // Morning rock
            0xaaa0a0,   // Pink stone
            0x8a8080,   // Cool gray
            0xbab0b0    // Pale boulder
        ],
        shrubColors: [
            0x4a6a4a,   // Morning palm
            0x5a7a5a,   // Soft green
            0x3a5a3a,   // Cool shadow
            0x5a7a58    // Dewy frond
        ]
    }
];

export class CoastalBiome extends BiomeRenderer {
    constructor(scene, camera, worldTranslator, config = {}) {
        // Coastal-specific configuration
        const coastalConfig = createBiomeConfig({
            // ─────────────────────────────────────────────────────────────
            // ROAD - Winding coastal highway
            // ─────────────────────────────────────────────────────────────
            roadWidth: 10,                       // Standard coastal road
            chunkLength: 50,
            pathSegmentLength: 140,              // Medium curves
            pathLookAhead: 90,
            roadMaxLateralOffset: 200,           // Winding along coast
            roadMaxVerticalOffset: 30,           // Clifftop elevation changes
            roadSplineTension: 0.0,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN - Sandy cliffs and beaches
            // ─────────────────────────────────────────────────────────────
            terrainColor: 0xd2b894,              // Sandy tan
            terrainAmplitudeLarge: 100,          // Coastal cliffs
            terrainAmplitudeMedium: 45,          // Rolling dunes
            terrainAmplitudeSmall: 15,           // Beach detail
            terrainAmplitudeFine: 5,             // Sand texture
            terrainHeightMultiplier: 0.6,        // Medium height
            waterLevel: 5,                       // Visible water/ocean

            // ─────────────────────────────────────────────────────────────
            // TERRAIN DIMENSIONS
            // ─────────────────────────────────────────────────────────────
            terrainWidth: 1000,
            terrainSegmentsOut: 18,
            terrainSegmentsAlong: 1,

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE STRIP
            // ─────────────────────────────────────────────────────────────
            roadsideStripWidth: 35,
            roadsideStripSegments: 4,
            roadsideStripOverlap: 0.04,
            mountainOverlapIntoStrip: 6,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN FEATURES - Dunes and cliffs
            // ─────────────────────────────────────────────────────────────
            terrainFeaturesEnabled: true,
            terrainFeatureScale: 0.00003,
            terrainFeatureWeights: {
                dunes: 1.0,       // Sandy dunes
                peaks: 0.2,       // Occasional headlands
                plateaus: 0.5,    // Flat beach areas
                spires: 0.15,     // Sea stacks
                cliffs: 0.7,      // Coastal cliffs
                valleys: 0.4      // Beach coves
            },
            terrainFeatureBlend: 0.55,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN HEIGHT LAYERING
            // ─────────────────────────────────────────────────────────────
            terrainLayerHeights: [0.25, 0.45, 0.7, 1.0],
            terrainLayerDistances: [0.15, 0.4, 0.7, 1.0],

            // ─────────────────────────────────────────────────────────────
            // FLAT ZONE - Beach areas near road
            // ─────────────────────────────────────────────────────────────
            terrainFlatZoneMin: 0.015,
            terrainFlatZoneMax: 0.15,
            terrainBlendRange: 0.12,

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE OBJECTS - Palm trees and coastal rocks
            // ─────────────────────────────────────────────────────────────
            enableRoadsideObjects: true,
            roadsideObjectSpawnDistance: 700,
            roadsideObjectDensity: 0.35,         // Moderate vegetation
            roadsideObjectMinSafeZone: 10,
            roadsideObjectDistributionWidth: 160,
            rockProbability: 0.25,               // Some beach rocks
            rockMinSize: 2,
            rockMaxSize: 6,
            shrubMinSize: 6,                     // Palm trees
            shrubMaxSize: 16,
            shrubSpriteCount: 3,
            shrubScaleVariation: 0.3,
            shrubStretchVariation: 0.25,
            roadsideObjectFadeInDuration: 0.5,

            // Coastal sprites (using shrubs as placeholder)
            shrubSpritePaths: [
                'assets/sprites/shrub1.png',
                'assets/sprites/shrub2.png',
                'assets/sprites/shrub3.png'
            ],

            // Merge with any user-provided config
            ...config
        });

        // Validate configuration in development
        validateBiomeConfig(coastalConfig, 'CoastalBiome');

        super(scene, camera, worldTranslator, coastalConfig);

        // Biome identity
        this.biomeName = 'Coastal';
        this.minMiles = 2000;
        this.maxMiles = 2500;

        // Store biome-specific color palettes
        this.biomeColors = COASTAL_COLORS;
        this.palette.setBiomeColors(COASTAL_COLORS);
    }

    getTerrainColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.terrainColors : [];
    }

    getRockColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.rockColors : [];
    }

    getShrubColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.shrubColors : [];
    }
}

export const COASTAL_BIOME_ENTRY = {
    minMiles: 2000,
    maxMiles: 2500,
    BiomeClass: CoastalBiome,
    name: 'Coastal'
};
